import React, { useRef, useState, useEffect, memo } from "react";
import { isEmpty } from "lodash";
import classNames from "classnames";

import { COMMUNITY_TABLE_HEADERS, SORTED_UPGRADES_TYPES } from "utils/communities/constants";
import { getSavingsTableData, getStartAndEnd, getWaterfallData, isHidden } from "utils/communities";
import { buildNiceLinearTicks } from "utils/chartNiceTicks";
import { useStateSelector } from "utils/field";

import ReactTooltip from "react-tooltip";
import SyncScrollBar from "components/SyncScrollBar";
import Tooltip from "components/Tooltip";
import Filters from "./Filters";
import NoData from "../NoData";
import SavingsByMeasureTable from "./SavingsTable";
import WaterfallRunGapsSummary from "./WaterfallRunGapsSummary";

import classes from "./styles.module.scss";

import ArrowDown from "assets/images/icons/JSX/arrow-down";

const BAR_ANIMATION_DURATION = 500;
const BAR_STAGGER_DELAY = 0.06;

const AnimatedBarValue = ({ value = 0, delay = 0, suffix = "", decimals = 2 }) => {
    const [displayValue, setDisplayValue] = useState(0);

    useEffect(() => {
        setDisplayValue(0);

        const timeout = setTimeout(() => {
            const startTime = performance.now();

            const animate = (currentTime) => {
                const elapsed = currentTime - startTime;
                const progress = Math.min(elapsed / BAR_ANIMATION_DURATION, 1);
                const eased = 1 - Math.pow(1 - progress, 3);

                setDisplayValue(value * eased);

                if (progress < 1) {
                    requestAnimationFrame(animate);
                }
            };

            requestAnimationFrame(animate);
        }, delay * 1000);

        return () => clearTimeout(timeout);
    }, [value, delay]);

    return (
        <>
            {displayValue.toFixed(decimals)} {suffix}
        </>
    );
};

const MAX_AXIS_TICKS = 11;

/** Same step semantics as pre–unit-switch waterfall (10% ticks, wider step if range > 10 slots). */
const LEGEND_ITEMS_STEP = 10;

/**
 * % axis matching legacy waterfall: always 0…100 when cumulative savings never go below 0; if cumulative
 * savings can exceed 100% (negative running total), top rounds up to the next 20. Grid/legend uses 10% steps
 * unless the range forces a coarser step (see {@link LEGEND_ITEMS_STEP}).
 */
const buildLegacyPercentWaterfallAxis = (cumulativeStepTotals) => {
    const lowestPercentage =
        cumulativeStepTotals.length > 0 ? Math.min(...cumulativeStepTotals) : 100;

    const scaleMin = 0;
    const scaleMax =
        lowestPercentage >= 0
            ? 100
            : Math.ceil((100 + Math.abs(lowestPercentage)) / 20) * 20;

    const scaleRange = Math.max(1e-9, scaleMax - scaleMin);
    const adjustedStep = scaleRange / LEGEND_ITEMS_STEP > 10 ? scaleRange / 10 : LEGEND_ITEMS_STEP;

    const ticks = [];
    for (let i = scaleMin; i <= scaleMax + 1e-9; i += adjustedStep) {
        ticks.push(Number.parseFloat(Number(i).toPrecision(12)));
    }

    return { scaleMin, scaleMax, scaleRange, ticks, step: adjustedStep };
};

/**
 * Absolute axis: bottom 0 unless data goes meaningfully negative; top at least 110% of baseline (denom × 1.1).
 * Tiny negative mins from floating-point in bar positions are treated as 0 so we do not draw sub-zero gridlines.
 */
const buildWaterfallAbsoluteAxisExtents = (dataMin, dataMax, denom, maxTicks) => {
    const noiseFloor = Math.max(1e-9, Math.abs(Number(denom) || 0) * 1e-12);
    const minBound = dataMin < -noiseFloor ? dataMin : 0;
    const hiNeed = Math.max(denom * 1.1, dataMax);
    const hasNegative = dataMin < -noiseFloor;

    if (hasNegative) {
        return buildNiceLinearTicks(minBound, hiNeed, maxTicks);
    }

    const nice = buildNiceLinearTicks(0, hiNeed, maxTicks);
    let { scaleMax, ticks, step } = nice;
    const minTop = denom * 1.1;
    scaleMax = Math.max(scaleMax, minTop, dataMax);
    if (scaleMax > nice.scaleMax + 1e-9) {
        const again = buildNiceLinearTicks(0, scaleMax, maxTicks);
        return { scaleMin: 0, scaleMax: again.scaleMax, ticks: again.ticks, step: again.step };
    }
    return { scaleMin: 0, scaleMax, ticks, step };
};

const formatAbsoluteAxisTick = (v, isEnergy, step) => {
    const abs = Math.abs(v);
    if (isEnergy) {
        if (abs >= 10000) return `${(v / 1000).toFixed(1)}k GJ`;
        if (step >= 10) return `${Math.round(v)} GJ`;
        if (step >= 1) return `${Math.round(v)} GJ`;
        if (step >= 0.1) return `${v.toFixed(1)} GJ`;
        return `${v.toFixed(2)} GJ`;
    }
    if (abs >= 1000) return `${(v / 1000).toFixed(2)}k tCO₂`;
    if (step >= 1) return `${Math.round(v)} tCO₂`;
    if (step >= 0.1) return `${v.toFixed(1)} tCO₂`;
    return `${v.toFixed(2)} tCO₂`;
};

const LABEL_WIDTH = "175px";
const GRAPH_HEIGHT = "70vh";
const BAR_AREA_WIDTH = `calc(${GRAPH_HEIGHT} - ${LABEL_WIDTH})`;
const TOP_PADDING = "30px";
const CONTENT_WIDTH = `calc(${GRAPH_HEIGHT} + ${TOP_PADDING})`;
const MIN_ROW_HEIGHT = 50;

/** Whole-package GJ-weighted savings reference (vertical dashed line + legend). Off for now — set to `true` to restore. */
const SHOW_WATERFALL_PACKAGE_REFERENCE_LINE = false;

/** Whole-package GJ-weighted savings reference (vertical guide). */
const WATERFALL_CHECKPOINT_LINE_META = [{ key: "TOTAL", label: "Whole package" }];

const WATERFALL_CHECKPOINT_LINE_COLORS = {
    TOTAL: "#c62828",
};

const BottomGraph = memo(
    ({
        allModels = [],
        allUpgradePackages = [],
        runData = null,
        modelUpgradesRun = null,
        fuelPrices = {},
        communityModelCountsMap = {},
        applyModelResultMultiplier,
    }) => {
        const viewportRef = useRef(null);
        const wrapperRef = useRef(null);
        const labelRefs = useRef([]);

        const [wrapperWidth, setWrapperWidth] = useState(0);
        const [selectedModels, setSelectedModels] = useState(allModels.map((model) => model.value));
        const [selectedMetric, setSelectedMetric] = useState("energySavings"); // energySavings, emissionsSavings
        const [selectedUpgradePackage, setSelectedUpgradePackage] = useState(allUpgradePackages[0]?.value || null);
        const [hideNAValues, setHideNAValues] = useState(false);
        const [waterfallData, setWaterfallData] = useState([]);
        const [waterfallRunGaps, setWaterfallRunGaps] = useState(null);
        const [waterfallCheckpoints, setWaterfallCheckpoints] = useState(null);
        const [tableData, setTableData] = useState([]);
        const [visibleWaterfallDataSorted, setVisibleWaterfallDataSorted] = useState([]);
        const [measureOrder, setMeasureOrder] = useState(SORTED_UPGRADES_TYPES);
        /** `percent` = % of community baseline (default); `absolute` = GJ or tCO₂ vs baseline total when denominators exist. */
        const [waterfallAxisUnit, setWaterfallAxisUnit] = useState("percent");
        const [waterfallAxisMeta, setWaterfallAxisMeta] = useState(null);

        const { email = "" } = useStateSelector("user") || {};
        const isEnviroCentreUser = email.split("@")[1] === "@envirocentre.ca";
        const isEnergyPercent = selectedMetric === "energySavings";

        useEffect(() => {
            const el = wrapperRef.current;

            if (!el) return;

            const ro = new ResizeObserver(() => {
                setWrapperWidth(el.clientWidth);
            });

            ro.observe(el);
            setWrapperWidth(el.clientWidth);

            return () => ro.disconnect();
        }, []);

        useEffect(() => {
            if (!runData?.runResultsByMeasure) {
                setWaterfallRunGaps(null);
                setWaterfallCheckpoints(null);
                setWaterfallAxisMeta(null);
                return;
            }

            const tableData = getSavingsTableData(
                runData?.runResultsByMeasure,
                selectedUpgradePackage,
                selectedModels,
                communityModelCountsMap,
                applyModelResultMultiplier
            );

            const {
                waterfall: nextWaterfall,
                runGaps,
                checkpoints,
                axis,
            } = getWaterfallData(
                Object.values(runData?.runResults || {}),
                runData?.runResultsByMeasure,
                selectedUpgradePackage,
                selectedModels,
                communityModelCountsMap,
                applyModelResultMultiplier
            );

            setWaterfallData(nextWaterfall);
            setWaterfallRunGaps(runGaps ?? null);
            setWaterfallCheckpoints(checkpoints ?? null);
            setWaterfallAxisMeta(axis ?? null);
            setTableData(tableData);
        }, [
            runData?.runResults,
            runData?.runResultsByMeasure,
            selectedUpgradePackage,
            selectedModels,
            communityModelCountsMap,
            applyModelResultMultiplier,
        ]);

        useEffect(() => {
            setWaterfallAxisUnit("percent");
        }, [selectedMetric]);

        useEffect(() => {
            const denom = isEnergyPercent ? waterfallAxisMeta?.denomSiteGJ : waterfallAxisMeta?.denomEmissions;
            if (waterfallAxisUnit === "absolute" && !(denom > 0)) {
                setWaterfallAxisUnit("percent");
            }
        }, [waterfallAxisMeta, waterfallAxisUnit, isEnergyPercent]);

        useEffect(() => {
            ReactTooltip.rebuild();
        }, [
            waterfallData,
            waterfallCheckpoints,
            selectedUpgradePackage,
            selectedMetric,
            hideNAValues,
            waterfallAxisUnit,
        ]);

        const visibleWaterfallData = React.useMemo(
            () => waterfallData.filter((item) => !hideNAValues || !isHidden(item, "waterfall", isEnergyPercent)),
            [waterfallData, hideNAValues, isEnergyPercent]
        );

        const visibleTableData = React.useMemo(
            () => tableData.filter((item) => !hideNAValues || !isHidden(item, "table")),
            [tableData, hideNAValues]
        );

        // Sort and recalculate start/end for every bar (cumulative waterfall) when visible data or user order changes.
        useEffect(() => {
            const sortedData = [...visibleWaterfallData];

            sortedData.sort((a, b) => measureOrder.indexOf(a.upgradeType) - measureOrder.indexOf(b.upgradeType));
            sortedData.forEach((item, idx) => {
                sortedData[idx] = {
                    ...item,
                    ...getStartAndEnd(sortedData, idx, item),
                };
            });

            setVisibleWaterfallDataSorted(sortedData);
        }, [visibleWaterfallData, measureOrder]);

        const waterfallLayout = React.useMemo(() => {
            const rows = visibleWaterfallDataSorted;
            const cumulativePercent = [];
            let runPct = 0;
            rows.forEach((item) => {
                const d = isEnergyPercent
                    ? (item.averagePercentEnergySaving ?? 0)
                    : (item.averagePercentEmissionSaving ?? 0);
                runPct += d;
                cumulativePercent.push(runPct);
            });

            const denom = isEnergyPercent ? waterfallAxisMeta?.denomSiteGJ ?? 0 : waterfallAxisMeta?.denomEmissions ?? 0;
            const useAbsolute = waterfallAxisUnit === "absolute" && denom > 0;

            if (!useAbsolute) {
                const { scaleMin, scaleMax, scaleRange, ticks, step } = buildLegacyPercentWaterfallAxis(cumulativePercent);
                const tickModels = ticks.map((t) => ({
                    value: t,
                    label: `${Math.round(t)}%`,
                }));
                const layoutRows = rows.map((item) => ({
                    item,
                    start: isEnergyPercent ? item.startEnergy : item.startEmissions,
                    end: isEnergyPercent ? item.endEnergy : item.endEmissions,
                    barValue:
                        (isEnergyPercent ? item.averagePercentEnergySaving : item.averagePercentEmissionSaving) ?? 0,
                }));
                return {
                    unit: "percent",
                    scaleMin,
                    scaleMax,
                    scaleRange,
                    ticks: tickModels,
                    step,
                    layoutRows,
                    cumulativePercent,
                    cumulativeAbs: null,
                    barDecimals: 2,
                    barSuffix: "%",
                };
            }

            let rem = denom;
            const layoutRows = rows.map((item) => {
                const pct = isEnergyPercent
                    ? (item.averagePercentEnergySaving ?? 0)
                    : (item.averagePercentEmissionSaving ?? 0);
                const deltaAbs = ((Number(pct) || 0) / 100) * denom;
                const start = rem;
                rem -= deltaAbs;
                const end = rem;
                return {
                    item,
                    start,
                    end,
                    barValue: deltaAbs,
                };
            });
            const cumulativeAbs = [];
            let runAbs = 0;
            layoutRows.forEach((row) => {
                runAbs += row.barValue;
                cumulativeAbs.push(runAbs);
            });
            const allAxis = [0, denom, ...layoutRows.flatMap((r) => [r.start, r.end])];
            const lo = Math.min(...allAxis);
            const hi = Math.max(...allAxis);
            const { scaleMin, scaleMax, ticks, step } = buildWaterfallAbsoluteAxisExtents(lo, hi, denom, MAX_AXIS_TICKS);
            const scaleRange = Math.max(1e-9, scaleMax - scaleMin);
            const tickModels = ticks.map((t) => ({
                value: t,
                label: formatAbsoluteAxisTick(t, isEnergyPercent, step),
            }));
            const barDecimals = 1;
            const barSuffix = isEnergyPercent ? "GJ" : "tCO₂";
            return {
                unit: "absolute",
                scaleMin,
                scaleMax,
                scaleRange,
                ticks: tickModels,
                step,
                layoutRows,
                cumulativePercent,
                cumulativeAbs,
                barDecimals,
                barSuffix,
            };
        }, [
            visibleWaterfallDataSorted,
            isEnergyPercent,
            waterfallAxisUnit,
            waterfallAxisMeta?.denomSiteGJ,
            waterfallAxisMeta?.denomEmissions,
        ]);

        /** Vertical guides in the bar area: same axis as bars (remaining baseline vs package savings). */
        const checkpointOverlayLines = React.useMemo(() => {
            if (!SHOW_WATERFALL_PACKAGE_REFERENCE_LINE) {
                return [];
            }
            if (!waterfallCheckpoints || !Number.isFinite(waterfallLayout.scaleMax)) {
                return [];
            }
            const { scaleMin, scaleMax, scaleRange, unit } = waterfallLayout;
            if (scaleRange <= 0) {
                return [];
            }
            const denomCp = isEnergyPercent ? waterfallAxisMeta?.denomSiteGJ : waterfallAxisMeta?.denomEmissions;
            return WATERFALL_CHECKPOINT_LINE_META.map(({ key, label }) => {
                const entry = waterfallCheckpoints[key];
                if (!entry) {
                    return null;
                }
                const savingsPct = isEnergyPercent ? entry.percentEnergySavings : entry.percentEmissionSavings;
                if (savingsPct == null || !Number.isFinite(Number(savingsPct))) {
                    return null;
                }
                const S = Number(savingsPct);
                const remaining =
                    unit === "absolute" && denomCp > 0 ? (denomCp * (100 - S)) / 100 : 100 - S;
                const pos = Math.min(scaleMax, Math.max(scaleMin, remaining));
                const leftPct = ((pos - scaleMin) / scaleRange) * 100;
                const metricShort = isEnergyPercent ? "E" : "GHG";
                return {
                    key,
                    label,
                    leftPct,
                    savingsPct: S,
                    color: WATERFALL_CHECKPOINT_LINE_COLORS[key] || "#455a64",
                    metricShort,
                };
            }).filter(Boolean);
        }, [waterfallCheckpoints, isEnergyPercent, waterfallLayout, waterfallAxisMeta?.denomSiteGJ, waterfallAxisMeta?.denomEmissions]);

        const getBarStyle = (start, end, barValue) => {
            const { scaleMin, scaleMax, scaleRange } = waterfallLayout;
            const leftVal = Math.min(start, end);
            const widthVal = Math.abs(end - start);
            const leftPct = ((leftVal - scaleMin) / scaleRange) * 100;
            const widthPct = (widthVal / scaleRange) * 100;
            const isNoUpgrade = widthVal === 0;
            const isPositive = barValue > 0;

            const widthPctFinal = isNoUpgrade ? 0 : Math.max(widthPct, 0.5);

            return {
                left: `${leftPct}%`,
                width: isNoUpgrade ? "1px" : `${widthPctFinal}%`,
                isNoUpgrade,
                labelLeft: `${leftPct}%`,
                barClass: isNoUpgrade
                    ? classes.waterfallFlatLine
                    : isPositive
                      ? classes.waterfallBarPositive
                      : classes.waterfallBarNegative,
            };
        };

        const handleSort = (index, amount) => {
            const neighborIndex = index + amount;
            if (neighborIndex < 0 || neighborIndex >= visibleWaterfallDataSorted.length) {
                return;
            }

            const typeMoving = visibleWaterfallDataSorted[index]?.upgradeType;
            const typeNeighbor = visibleWaterfallDataSorted[neighborIndex]?.upgradeType;
            if (!typeMoving || !typeNeighbor) {
                return;
            }

            // Reorder only `measureOrder` using full-type-list indices. The visible chart has fewer rows when
            // "Hide N/A" is on — never splice `visibleWaterfallDataSorted` with measureOrder indices (that corrupts
            // the array and breaks getStartAndEnd). useEffect will resort and recompute all bar positions.
            const mo = [...measureOrder];
            const iMove = mo.indexOf(typeMoving);
            const iNeighbor = mo.indexOf(typeNeighbor);
            if (iMove === -1 || iNeighbor === -1) {
                return;
            }

            mo.splice(iMove, 1);
            const iNeighborAfter = mo.indexOf(typeNeighbor);
            if (amount > 0) {
                mo.splice(iNeighborAfter + 1, 0, typeMoving);
            } else {
                mo.splice(iNeighborAfter, 0, typeMoving);
            }

            setMeasureOrder(mo);
        };

        const minNeededWidth = visibleWaterfallData.length * MIN_ROW_HEIGHT;
        const viewportWidth = Math.max(wrapperWidth, minNeededWidth);
        const contentHeight = viewportWidth;

        return (
            <div className={classes.bottomGraphContainer}>
                <div className={classes.stickyHeader}>
                    <span className={classes.bottomGraphTitle}>Savings By Measure</span>
                    <Filters
                        allModels={allModels}
                        allUpgradePackages={allUpgradePackages}
                        selectedModels={selectedModels}
                        setSelectedModels={setSelectedModels}
                        selectedMetric={selectedMetric}
                        setSelectedMetric={setSelectedMetric}
                        waterfallAxisUnit={waterfallAxisUnit}
                        setWaterfallAxisUnit={setWaterfallAxisUnit}
                        waterfallAbsoluteAvailable={
                            (isEnergyPercent ? waterfallAxisMeta?.denomSiteGJ : waterfallAxisMeta?.denomEmissions) > 0
                        }
                        selectedUpgradePackage={selectedUpgradePackage}
                        setSelectedUpgradePackage={setSelectedUpgradePackage}
                        hideNAValues={hideNAValues}
                        setHideNAValues={setHideNAValues}
                    />
                </div>
                <div className={classes.bottomGraphWrapper}>
                    <div className={classes.bottomGraphLeftBarLegend}>
                        <div style={{ height: CONTENT_WIDTH }}>
                            <div style={{ height: TOP_PADDING, flexShrink: 0 }} />
                            <div
                                className={classes.bottomGraphLeftBarLegendContainer}
                                style={{ height: BAR_AREA_WIDTH }}
                            >
                                <span className={classes.bottomGraphLeftBarLegendTitle}>
                                    {waterfallLayout.unit === "percent"
                                        ? isEnergyPercent
                                            ? "Energy savings (%)"
                                            : "GHG savings (%)"
                                        : isEnergyPercent
                                          ? "Energy savings (GJ)"
                                          : "GHG savings (tCO₂)"}
                                </span>
                                <div className={classes.bottomGraphLeftBarLegendItemsContainer}>
                                    {waterfallLayout.ticks.map((tick, index) => (
                                        <div
                                            key={`legend-${tick.value}-${index}`}
                                            className={classes.bottomGraphLeftBarLegendItem}
                                            style={{
                                                top: `${((waterfallLayout.scaleMax - tick.value) / waterfallLayout.scaleRange) * 100}%`,
                                            }}
                                        >
                                            <span>{tick.label}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className={classes.bottomGraphLeftBarLegendBottom} />
                        </div>
                    </div>
                    <div className={classes.bottomGraphItemsWrapper} ref={wrapperRef} style={{ position: "relative" }}>
                        {isEmpty(waterfallData) && (
                            <NoData text="There is no data available for these selected models and upgrade package." />
                        )}
                        <div
                            ref={viewportRef}
                            className={classes.bottomGraphRotatedViewport}
                            style={{ width: viewportWidth, height: CONTENT_WIDTH }}
                        >
                            <div
                                className={classes.bottomGraphRotatedContent}
                                style={{
                                    width: CONTENT_WIDTH,
                                    height: contentHeight,
                                    transform: `translateY(${CONTENT_WIDTH}) rotate(-90deg)`,
                                }}
                            >
                                <div className={classes.gridLinesOverlay}>
                                    {waterfallLayout.ticks.map((tick, index) => (
                                        <div
                                            key={`grid-${tick.value}-${index}`}
                                            className={classes.gridLine}
                                            style={{
                                                left: `${((tick.value - waterfallLayout.scaleMin) / waterfallLayout.scaleRange) * 100}%`,
                                            }}
                                        />
                                    ))}
                                    {!isEmpty(waterfallData) &&
                                        checkpointOverlayLines.map((line) => (
                                            <div
                                                key={`cp-${line.key}`}
                                                className={classes.checkpointLine}
                                                style={{
                                                    left: `${line.leftPct}%`,
                                                    "--checkpoint-line-color": line.color,
                                                }}
                                                title={`${line.label} · ${line.metricShort} ${line.savingsPct.toFixed(2)}% savings`}
                                            />
                                        ))}
                                </div>
                                {waterfallLayout.layoutRows.map((row, index) => {
                                    const { item, start, end, barValue } = row;
                                    const barStyle = getBarStyle(start, end, barValue);
                                    const delayIndex = item.delayIndex ?? index;
                                    const cumPct = waterfallLayout.cumulativePercent[index];
                                    const cumAbs = waterfallLayout.cumulativeAbs?.[index];
                                    const tooltipCumulative =
                                        waterfallLayout.unit === "absolute" && cumAbs != null
                                            ? `${cumAbs.toFixed(waterfallLayout.barDecimals)} ${waterfallLayout.barSuffix} cumulative (${cumPct?.toFixed(2) ?? "—"}% of baseline)`
                                            : `${cumPct?.toFixed(2) ?? "—"}% overall`;

                                    return (
                                        <div
                                            key={`${selectedUpgradePackage ?? ""}-${item.upgradeType}-${selectedMetric}-${waterfallLayout.unit}-${index}`}
                                            className={classes.bottomGraphItemContainer}
                                        >
                                            <div className={classes.bottomGraphItemLabel}>
                                                <div
                                                    className={classNames(classes.moveColumn, {
                                                        [classes.firstPackage]: index === 0,
                                                        [classes.lastPackage]:
                                                            index === visibleWaterfallDataSorted.length - 1,
                                                    })}
                                                >
                                                    <button
                                                        onClick={() => handleSort(index, -1)}
                                                        className="normalize-button"
                                                        data-for={`${item.upgradeType}-label`}
                                                        data-tip="Move left"
                                                        data-place="bottom"
                                                        disabled={index === 0}
                                                    >
                                                        <ArrowDown />
                                                    </button>
                                                    <button
                                                        onClick={() => handleSort(index, 1)}
                                                        className="normalize-button"
                                                        data-for={`${item.upgradeType}-label`}
                                                        data-tip="Move right"
                                                        data-place="bottom"
                                                        disabled={index === visibleWaterfallDataSorted.length - 1}
                                                    >
                                                        <ArrowDown />
                                                    </button>
                                                </div>
                                                <span>{COMMUNITY_TABLE_HEADERS[item.upgradeType]}</span>
                                            </div>
                                            <div
                                                className={classes.bottomGraphItemValue}
                                                onMouseEnter={() => {
                                                    const el = labelRefs.current[index];
                                                    if (el) ReactTooltip.show(el);
                                                }}
                                                onMouseLeave={() => {
                                                    const el = labelRefs.current[index];
                                                    if (el) ReactTooltip.hide(el);
                                                }}
                                            >
                                                <div className={classes.barTrack}>
                                                    <div
                                                        className={classNames(classes.waterfallBar, barStyle.barClass, {
                                                            [classes.enviroCentre]: isEnviroCentreUser,
                                                        })}
                                                        style={{
                                                            left: barStyle.left,
                                                            "--bar-width": barStyle.width,
                                                            animationDelay: `${delayIndex * BAR_STAGGER_DELAY}s`,
                                                        }}
                                                    />
                                                    <span
                                                        ref={(el) => (labelRefs.current[index] = el)}
                                                        className={classes.barPercentLabel}
                                                        style={{
                                                            left: barStyle.labelLeft,
                                                            animationDelay: `${delayIndex * BAR_STAGGER_DELAY}s`,
                                                        }}
                                                        data-tip={tooltipCumulative}
                                                        data-for="waterfall-tooltip"
                                                    >
                                                        <AnimatedBarValue
                                                            value={barValue}
                                                            delay={delayIndex * BAR_STAGGER_DELAY}
                                                            decimals={waterfallLayout.barDecimals}
                                                            suffix={waterfallLayout.barSuffix}
                                                        />
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
                {!isEmpty(waterfallData) && checkpointOverlayLines.length > 0 && (
                    <div className={classes.checkpointLegend} aria-label="Whole package savings reference line">
                        <span className={classes.checkpointLegendTitle}>Package reference</span>
                        {checkpointOverlayLines.map((line) => (
                            <span key={line.key} className={classes.checkpointLegendItem}>
                                <span className={classes.checkpointLegendSwatch} style={{ borderColor: line.color }} />
                                <span>
                                    {line.label}: {line.savingsPct.toFixed(2)}% {line.metricShort}
                                </span>
                            </span>
                        ))}
                    </div>
                )}
                {/* This component is for diagnostic purposes only, not for deployment */}
                {/* <WaterfallRunGapsSummary runGaps={waterfallRunGaps} /> */}
                <SyncScrollBar targetRef={wrapperRef} />
                <Tooltip id="waterfall-tooltip" place="bottom" />
                <SavingsByMeasureTable visibleTableData={visibleTableData} isEnviroCentreUser={isEnviroCentreUser} />
            </div>
        );
    }
);

export default BottomGraph;
